import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useDrag } from 'react-dnd';
import styles from './index.module.scss';

const DraggableElement = ({
    element,
    index,
    isSelected,
    isMultiSelected = false,
    onSelect,
    onChange,
    onDoubleClick,
    canvasWidth,
    canvasHeight
}) => {
    const ref = useRef(null);
    const initialPos = useRef({ x: 0, y: 0 });
    const [isResizing, setIsResizing] = useState(false);
    const [isCtrlPressed, setIsCtrlPressed] = useState(false);
    const currentHandleRef = useRef(null);

    const [{ isDragging }, drag, dragPreview] = useDrag({
        type: 'ELEMENT',
        item: () => {
            return {
                index,
                type: 'ELEMENT',
                isCopy: isCtrlPressed
            };
        },
        canDrag: !isResizing && (isSelected || isMultiSelected),
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    });

    useEffect(() => {
        dragPreview(<div></div>); // 隐藏拖拽预览
    }, [dragPreview]);

    // 监听Ctrl键状态
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey && !isCtrlPressed) {
                setIsCtrlPressed(true);
            }
        };

        const handleKeyUp = (e) => {
            if (!e.ctrlKey && isCtrlPressed) {
                setIsCtrlPressed(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [isCtrlPressed]);

    const onMouseMove = useCallback((e) => {
        if (!isResizing) return;

        const dx = e.clientX - initialPos.current.x;
        const dy = e.clientY - initialPos.current.y;
        let newWidth = initialPos.current.width;
        let newHeight = initialPos.current.height;
        let newX = initialPos.current.left;
        let newY = initialPos.current.top;

        switch (currentHandleRef.current) {
            case 'nw':
                newWidth = initialPos.current.width - dx;
                newHeight = initialPos.current.height - dy;
                newX = initialPos.current.left + dx;
                newY = initialPos.current.top + dy;
                break;
            case 'ne':
                newWidth = initialPos.current.width + dx;
                newHeight = initialPos.current.height - dy;
                newY = initialPos.current.top + dy;
                break;
            case 'sw':
                newWidth = initialPos.current.width - dx;
                newHeight = initialPos.current.height + dy;
                newX = initialPos.current.left + dx;
                break;
            case 'se':
                newWidth = initialPos.current.width + dx;
                newHeight = initialPos.current.height + dy;
                break;
        }

        // 确保元素不会太小
        if (newWidth >= 20 && newHeight >= 20) {
            // 确保不会超出画布边界
            newX = Math.max(0, Math.min(newX, canvasWidth - newWidth));
            newY = Math.max(0, Math.min(newY, canvasHeight - newHeight));

            onChange({
                x: newX,
                y: newY,
                w: newWidth,
                h: newHeight
            });
        }
    }, [isResizing, element, onChange, canvasWidth, canvasHeight]);

    const onMouseUp = useCallback(() => {
        setIsResizing(false);
        currentHandleRef.current = null;
    }, []);

    useEffect(() => {
        if (!ref.current) return;

        const elementRef = ref.current;

        const onMouseDown = (e) => {
            const handle = e.target.getAttribute('data-handle');
            if (handle) {
                setIsResizing(true);
                currentHandleRef.current = handle;

                // 获取画布的缩放比例
                const canvas = elementRef.closest('[data-canvas]') || elementRef.parentElement;
                const canvasRect = canvas.getBoundingClientRect();
                const scale = canvasRect.width / canvasWidth;

                initialPos.current = {
                    x: e.clientX,
                    y: e.clientY,
                    width: element.w,
                    height: element.h,
                    left: element.x,
                    top: element.y,
                    scale: scale
                };
                e.stopPropagation();
                e.preventDefault();
            } else {
                // 如果点击的不是调整大小手柄，则选中元素
                onSelect(e.ctrlKey || e.metaKey);
            }
        };

        const onDoubleClickHandler = (e) => {
            e.stopPropagation();
            e.preventDefault();
            if (onDoubleClick && !isResizing) {
                onDoubleClick(index, element);
            }
        };

        elementRef.addEventListener('mousedown', onMouseDown);
        elementRef.addEventListener('dblclick', onDoubleClickHandler);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        return () => {
            elementRef.removeEventListener('mousedown', onMouseDown);
            elementRef.removeEventListener('dblclick', onDoubleClickHandler);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [element, onChange, isResizing, onMouseMove, onMouseUp, onSelect, onDoubleClick, canvasWidth]);

    drag(ref);

    // 构建CSS属性
    const buildBoxShadow = () => {
        const { boxShadowX, boxShadowY, boxShadowBlur, boxShadowSpread, boxShadowColor } = element.ex || {};
        if (boxShadowX !== undefined || boxShadowY !== undefined || boxShadowBlur !== undefined) {
            const x = boxShadowX || 0;
            const y = boxShadowY || 0;
            const blur = boxShadowBlur || 0;
            const spread = boxShadowSpread || 0;
            const color = boxShadowColor || '#000000';
            return `${x}px ${y}px ${blur}px ${spread}px ${color}`;
        }
        return element.ex?.boxShadow;
    };

    const buildTransform = () => {
        const { rotate, rotateX, rotateY, scaleX, scaleY, skewX, skewY } = element.ex || {};
        if (rotate !== undefined || rotateX !== undefined || rotateY !== undefined || scaleX !== undefined || scaleY !== undefined || skewX !== undefined || skewY !== undefined) {
            const r = rotate || 0;
            const rx = rotateX || 0;
            const ry = rotateY || 0;
            const sx = scaleX || 1;
            const sy = scaleY || 1;
            const skX = skewX || 0;
            const skY = skewY || 0;
            return `rotateX(${rx}deg) rotateY(${ry}deg) rotate(${r}deg) scale(${sx}, ${sy}) skew(${skX}deg, ${skY}deg)`;
        }
        return element.ex?.transform;
    };

    // 构建动画样式
    const buildAnimationStyle = () => {
        if (!element.a) return {};

        const animationStyles = {
            '--animation-duration': `${element.animationDuration || 2}s`,
            '--animation-delay': `${element.animationDelay || 0}s`
        };

        return animationStyles;
    };

    // 应用样式
    const elementStyle = {
        left: `${element.x}px`,
        top: `${element.y}px`,
        width: `${element.w}px`,
        height: `${element.h}px`,
        opacity: isDragging ? 0.5 : (element.ex?.opacity || 1),
        position: 'absolute',
        // 应用所有扩展样式
        ...element.ex,
        // 重写复合属性
        boxShadow: buildBoxShadow(),
        transform: buildTransform(),
        // 应用动画相关的CSS变量
        ...buildAnimationStyle(),
        // 多选状态下的额外视觉效果
        ...(isMultiSelected && !isSelected ? {
            outline: '2px solid #1890ff',
            outlineOffset: '2px'
        } : {})
    };

    const elementClasses = `
        ${styles.element} 
        element 
        ${isSelected ? styles.selected : ''} 
        ${isMultiSelected ? styles.multiSelected : ''}
        ${element.groupId ? styles.grouped : ''}
        ${isDragging ? styles.dragging : ''}
        ${isCtrlPressed && isSelected ? styles.copyMode : ''}
        ${element.a ? element.a : ''}
    `.trim();

    // 渲染元素内容
    const renderContent = () => {
        if (element.i !== undefined) {
            // 图片元素 - 图片本身不应用边框等样式，这些由外层容器处理
            return (
                <img
                    src={element.i}
                    alt="图片元素"
                    className={styles.elementImage}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: element.ex?.objectFit || 'cover',
                        // 图片本身的borderRadius应该比容器稍小，避免边框显示问题
                        borderRadius: element.ex?.borderRadius ? `calc(${element.ex.borderRadius} - 1px)` : undefined
                    }}
                />
            );
        } else if (element.t !== undefined) {
            // 文字元素
            const textStyle = {
                fontSize: element.ex?.fontSize || '16px',
                color: element.ex?.color || '#000000',
                textAlign: element.ex?.textAlign || 'left',
                fontWeight: element.ex?.fontWeight || 'normal',
                lineHeight: element.ex?.lineHeight || '1.4',
                fontFamily: element.ex?.fontFamily,
                textShadow: element.ex?.textShadow,
                letterSpacing: element.ex?.letterSpacing,
                textDecoration: element.ex?.textDecoration,
                width: '100%',
                height: '100%',
                wordBreak: 'keep-all',
                display: 'flex',
                alignItems: 'center',
                // 根据textAlign设置justify-content
                justifyContent: element.ex?.textAlign === 'center' ? 'center' :
                    element.ex?.textAlign === 'right' ? 'flex-end' : 'flex-start',
                padding: '8px',
                boxSizing: 'border-box'
            };

            return (
                <div className={styles.elementText} style={textStyle}>
                    {element.t}
                </div>
            );
        }
        return null;
    };

    return (
        <div
            ref={ref}
            className={elementClasses}
            style={elementStyle}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            {renderContent()}

            {/* 调整大小手柄 */}
            {isSelected && (
                <>
                    <div
                        className={`${styles.resizeHandle} ${styles.nw} resize-handle`}
                        data-handle="nw"
                        data-export-ignore="true"
                    />
                    <div
                        className={`${styles.resizeHandle} ${styles.ne} resize-handle`}
                        data-handle="ne"
                        data-export-ignore="true"
                    />
                    <div
                        className={`${styles.resizeHandle} ${styles.sw} resize-handle`}
                        data-handle="sw"
                        data-export-ignore="true"
                    />
                    <div
                        className={`${styles.resizeHandle} ${styles.se} resize-handle`}
                        data-handle="se"
                        data-export-ignore="true"
                    />
                </>
            )}
        </div>
    );
};

export default DraggableElement;
